import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronRight, MapPin, Megaphone, Phone } from 'lucide-react';
import { Listing, OpeningHoursResult } from '../../types';
import { getCategory } from '../../config/categories';
import { mapPointOf } from '../../utils/geo';
import { isEmergency } from '../../utils/directorySearch';
import { activeMessages, displayAddress, pickLang, primaryPhone, subcategoryLabel, telLink } from '../../utils/listingDisplay';
import { ShiurOccurrence } from '../../utils/shiurTimes';
import { NextShiurText } from './ShiurTimeText';
import CategoryIcon from './CategoryIcon';
import DistanceText from './DistanceText';
import { ListingStatusTag, OpenStatusBadge } from './StatusBadge';

/** Shuls keep their one true address, /shul/:id (plan 8.2). */
export function listingHref(listing: Listing): string {
  if (listing.category === 'shul') return `/shul/${listing.id}`;
  return `/directory/${getCategory(listing.category).slug}/${listing.id}`;
}

/** The map with this listing's card open (like the shul list's map button). */
export function listingMapHref(listing: Listing): string {
  if (listing.category === 'shul') return `/shuls?view=map&shul=${listing.id}`;
  return `/directory/${getCategory(listing.category).slug}?view=map&item=${listing.id}`;
}

interface ListingCardProps {
  listing: Listing;
  hours: OpeningHoursResult | undefined;
  /** Meters from the user, after Near me. */
  distance?: number;
  /** Shiurim: the next time it is given. */
  nextShiur?: { date: string; occurrence: ShiurOccurrence };
}

export default function ListingCard({ listing, hours, distance, nextShiur }: ListingCardProps) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const name = pickLang(listing.nameEn, listing.nameHe, isHebrew);
  const address = displayAddress(listing, isHebrew);
  const phone = primaryPhone(listing);
  const emergency = isEmergency(listing);
  const hasMessage = activeMessages(listing.messages, new Date().toISOString()).length > 0;
  const href = listingHref(listing);

  return (
    <div
      className={`flex items-center gap-3 bg-paper rounded-[16px] border p-3 transition-colors ${
        emergency ? 'border-danger-500 border-2' : 'border-ink/10 hover:border-ink/30'
      }`}
    >
      <Link
        to={href}
        className="shrink-0 w-12 h-12 rounded-[10px] bg-fog flex items-center justify-center text-ink/70 overflow-hidden"
        tabIndex={-1}
        aria-hidden
      >
        {listing.photoUrl ? (
          <img src={listing.photoUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <CategoryIcon category={listing.category} className="w-6 h-6" strokeWidth={1.5} />
        )}
      </Link>

      <Link to={href} className="min-w-0 flex-1">
        <h2 className="font-semibold text-ink line-clamp-2 break-words">{name}</h2>
        {(address || distance !== undefined) && (
          <p className="text-sm text-ash truncate">
            {distance !== undefined && <DistanceText meters={distance} className="font-medium text-ink" />}
            {distance !== undefined && address && ' · '}
            {address}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-1 mt-1.5">
          <span className="text-xs px-2 py-0.5 rounded-[10px] bg-fog text-ink font-medium">
            {subcategoryLabel(listing, t, isHebrew)}
          </span>
          <OpenStatusBadge listing={listing} result={hours} />
          {listing.category === 'shiur' && <NextShiurText next={nextShiur} />}
          {hasMessage && (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-[10px] bg-weekday-50 text-weekday-700 font-medium">
              <Megaphone className="w-3 h-3" aria-hidden />
              {t('directory.detail.newMessage')}
            </span>
          )}
          <ListingStatusTag status={listing.status} />
        </div>
      </Link>

      {phone && (
        <a
          href={telLink(phone)}
          className={`shrink-0 flex items-center justify-center gap-1.5 rounded-full min-w-11 min-h-11 px-3 text-sm font-medium transition-colors ${
            emergency ? 'bg-danger-600 text-paper hover:bg-danger-700' : 'bg-fog text-ink hover:bg-ink/10'
          }`}
          aria-label={`${t('directory.action.call')} ${name}`}
        >
          <Phone className="w-4 h-4" aria-hidden />
          <span className={emergency ? '' : 'hidden sm:inline'}>{t('directory.action.call')}</span>
        </a>
      )}

      {mapPointOf(listing.location) && (
        <Link
          to={listingMapHref(listing)}
          className="shrink-0 flex items-center justify-center min-w-11 min-h-11 rounded-full text-ash hover:text-ink hover:bg-fog transition-colors"
          aria-label={t('shul.showOnMap')}
          title={t('shul.showOnMap')}
        >
          <MapPin className="w-4 h-4" aria-hidden />
        </Link>
      )}


      <Link to={href} className="shrink-0 hidden sm:block" aria-label={name}>
        <ChevronRight className="w-5 h-5 text-ash rtl:rotate-180" />
      </Link>
    </div>
  );
}
